/* «¿Dónde está tal chofer?», dicho sin afirmar de más.
 *
 * ── LA CADENA, Y DÓNDE SE CORTA ───────────────────────────────────────────
 *
 * Portería anota, en el mismo evento, QUIÉN cruzó y CON QUÉ UNIDAD. De ahí
 * sale todo: el chofer se fue con el 581, y dónde está el 581 lo sabe la
 * telemetría. La cadena es chofer → interno → posición.
 *
 * Lo que hay que tener claro es dónde se corta, porque cada eslabón es más
 * flojo que el anterior:
 *
 *   · «salió con el 581 el jueves»  → HECHO. Lo escribió una persona.
 *   · «el 581 está en Río Grande»   → HECHO, con su fecha.
 *   · «el chofer está en Río Grande»→ INFERENCIA. El chofer se baja.
 *
 * ── NO SE USA EL TRACTOR HABITUAL, Y ES LO MÁS IMPORTANTE DE ACÁ ──────────
 *
 * La pregunta invita a contestar «ese chofer anda siempre con el 581». Los
 * datos dicen que no alcanza: **3,1 tractores distintos por chofer** de
 * promedio, sólo 83 de 258 andan siempre con el mismo, y **82 de 258 (32%)
 * tienen su último cruce con una unidad distinta de la que más usan**.
 *
 * O sea que contestar por el habitual erraría de camión una de cada tres
 * veces — y el día que alguien pregunta dónde está un chofer es justo el día
 * en que agarró otro. Se usa el ÚLTIMO CRUCE, que es un hecho y no una
 * tendencia.
 *
 * ── Y ENTRAR A BASE NO DICE DÓNDE ESTÁ LA PERSONA ─────────────────────────
 *
 * Portería registra el paso del CAMIÓN, no el de la persona: nadie anota
 * cuando un chofer se va caminando. Así que «entró y no salió» significa que
 * el camión está en el playón — y el chofer lo más probable es que esté en su
 * casa. Son 139 de 379, o sea el grupo más grande: decir «está en base» de
 * todos ellos sería la respuesta equivocada la mayoría de las veces.
 */

import { mediodiaHoyAr } from "./fechaAr";

const ESTADOS = {
  /* Salió con esa unidad y la unidad no volvió a cruzar el portón. Es el único
   * caso en el que se puede decir algo sobre dónde está la persona. */
  con_unidad: {
    titulo: (u) => `Salió con el ${u.tractor}`,
    puedeUbicar: true,
  },
  /* El camión entró. De la persona no sabemos más — y no es un hueco de datos
   * que se pueda tapar: portería no anota al que se va a pie. */
  volvio_a_base: {
    titulo: (u) => `Entró a base con el ${u.tractor}`,
    puedeUbicar: false,
  },
  /* Salió con una unidad y esa unidad ya volvió a cruzar el portón. Puede
   * haber vuelto sin él, o haber salido con otro. El vínculo se rompió y
   * afirmar cualquier cosa sería inventar. */
  desvinculado: {
    titulo: (u) => `Salió con el ${u.tractor}, pero esa unidad ya volvió a cruzar`,
    puedeUbicar: false,
  },
  /* Cruzó sin unidad anotada: a pie, en un utilitario, de acompañante. */
  sin_unidad: {
    titulo: () => "Cruzó el portón sin unidad anotada",
    puedeUbicar: false,
  },
};

const mediodia = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);

export function diasDesde(fecha, hoy = mediodiaHoyAr()) {
  if (!fecha) return null;
  const f = new Date(String(fecha).slice(0, 10) + "T12:00:00");
  if (Number.isNaN(f.getTime())) return null;
  return Math.round((mediodia(hoy) - f) / 86400000);
}

/* CUÁNTO AGUANTA LA INFERENCIA.
 *
 * Un chofer que salió anteayer con el 581 muy probablemente siga con el 581.
 * Uno que salió hace tres semanas, no: los viajes al sur duran días, no
 * semanas, y si la unidad no volvió a cruzar en veinte días lo más probable es
 * que el dato esté incompleto y no que el camión siga afuera.
 *
 * DIEZ DÍAS es el techo, y sale del propio dato: el tramo más largo que la
 * planilla considera creíble son 15 días (`MAX_DIAS_ABIERTO` en FlotaBot), y
 * acá se corta antes porque lo que se afirma es sobre una PERSONA, que es lo
 * que más rápido se despega del camión.
 *
 * Pasado el techo no se miente ni se calla: se muestra el hecho —salió tal día
 * con tal unidad— y se deja de afirmar dónde está. */
export const DIAS_CONFIABLE = 10;

/* Devuelve siempre las mismas claves, haya dato o no. `vacio` es una clave y
 * no la ausencia de las otras: la pantalla dibuja un solo caso. */
export function dondeEsta(fila, hoy = mediodiaHoyAr()) {
  if (!fila || !fila.estado || !fila.fecha) {
    return {
      vacio: true,
      titulo: "Sin registro",
      detalle: "No figura cruzando el portón.",
      tractor: null,
      lugar: null,
      viejo: false,
    };
  }

  const cfg = ESTADOS[fila.estado] || ESTADOS.sin_unidad;
  const dias = diasDesde(fila.fecha, hoy);
  const viejo = dias !== null && dias > DIAS_CONFIABLE;

  /* El lugar sale SÓLO si la inferencia todavía se sostiene. Con el dato
   * viejo se sigue mostrando el hecho —salió con tal unidad tal día— y se
   * deja de decir dónde: es la diferencia entre «no lo sé» y «te digo
   * cualquier cosa». */
  const lugar = cfg.puedeUbicar && !viejo ? fila.lugar || null : null;

  return {
    vacio: false,
    titulo: cfg.titulo(fila),
    tractor: fila.tractor || null,
    lugar,
    viejo,
    detalle: [
      cfg.puedeUbicar ? fila.detalle || "" : "",
      viejo ? `Hace ${dias} días.` : "",
    ]
      .filter(Boolean)
      .join(" "),
  };
}
